/**
 * G.711 μ-law at 8 kHz — what the gateway's media socket carries, exactly as
 * a phone carrier sends it (2026-10-15). The browser test call converts at the
 * edge, so the assistant hears the same audio format a phone call gives it.
 */

export const PCMU_RATE = 8_000;
/** 20 ms at 8 kHz — one carrier frame. */
export const FRAME_SAMPLES = 160;

const BIAS = 0x84;
const CLIP = 32_635;

/** One linear 16-bit sample → μ-law byte. */
export function linearToMulaw(sample: number): number {
  let s = Math.max(-32_768, Math.min(32_767, Math.round(sample)));
  const sign = s < 0 ? 0x80 : 0;
  if (sign) s = -s;
  if (s > CLIP) s = CLIP;
  s += BIAS;
  let exponent = 7;
  for (let mask = 0x4000; (s & mask) === 0 && exponent > 0; mask >>= 1) exponent--;
  const mantissa = (s >> (exponent + 3)) & 0x0f;
  return ~(sign | (exponent << 4) | mantissa) & 0xff;
}

/** μ-law byte → linear 16-bit sample. */
export function mulawToLinear(byte: number): number {
  const u = ~byte & 0xff;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  const magnitude = (((mantissa << 3) + BIAS) << exponent) - BIAS;
  return sign ? -magnitude : magnitude;
}

/** Float samples (−1…1) → μ-law bytes. */
export function encodeMulaw(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = linearToMulaw(samples[i]! * 32_767);
  return out;
}

/** μ-law bytes → float samples (−1…1). */
export function decodeMulaw(bytes: Uint8Array): Float32Array {
  const out = new Float32Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = mulawToLinear(bytes[i]!) / 32_768;
  return out;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/**
 * Linear-interpolation resampler that keeps its phase across chunks, so a
 * stream cut into 20 ms frames plays without clicks at the joins.
 */
export class Upsampler {
  private last = 0;
  private pos = 0;

  constructor(
    private readonly from: number,
    private readonly to: number,
  ) {}

  push(input: Float32Array): Float32Array {
    const step = this.from / this.to;
    const out: number[] = [];
    // `pos` is measured from the sample BEFORE input[0] (the previous chunk's last).
    while (this.pos < input.length) {
      const i = Math.floor(this.pos);
      const frac = this.pos - i;
      const a = i === 0 ? this.last : input[i - 1]!;
      const b = input[i]!;
      out.push(a + (b - a) * frac);
      this.pos += step;
    }
    this.pos -= input.length;
    if (input.length > 0) this.last = input[input.length - 1]!;
    return Float32Array.from(out);
  }
}

/**
 * The microphone side, as an AudioWorklet: averages the context's samples down
 * to 8 kHz (a box filter — enough against aliasing for speech at telephone
 * bandwidth), encodes μ-law and posts one 160-byte frame every 20 ms.
 * Kept as source text so it loads from a Blob URL with no bundler step.
 */
export const CAPTURE_WORKLET = `
const BIAS = 0x84, CLIP = 32635;
function enc(x) {
  let s = Math.max(-32768, Math.min(32767, Math.round(x * 32767)));
  const sign = s < 0 ? 0x80 : 0;
  if (sign) s = -s;
  if (s > CLIP) s = CLIP;
  s += BIAS;
  let e = 7;
  for (let m = 0x4000; (s & m) === 0 && e > 0; m >>= 1) e--;
  return ~(sign | (e << 4) | ((s >> (e + 3)) & 0x0f)) & 0xff;
}
class PcmuCapture extends AudioWorkletProcessor {
  constructor() {
    super();
    this.ratio = sampleRate / 8000;
    this.acc = 0; this.n = 0; this.edge = this.ratio;
    this.frame = new Uint8Array(160); this.k = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    for (let i = 0; i < ch.length; i++) {
      this.acc += ch[i]; this.n++;
      this.edge -= 1;
      if (this.edge <= 0) {
        this.frame[this.k++] = enc(this.acc / this.n);
        this.acc = 0; this.n = 0; this.edge += this.ratio;
        if (this.k === 160) { this.port.postMessage(this.frame.slice(0)); this.k = 0; }
      }
    }
    return true;
  }
}
registerProcessor('pcmu-capture', PcmuCapture);
`;
